import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RouletteQuickDetails } from 'app/core/model/roulette-quick-details.model';
import { WebRouletteStatus } from 'app/core/model/web-roulette-status';
import { SubscriptionService } from 'app/public/services/subscription.service';
import { WebRouletteService } from 'app/public/services/web-roulette.service';
import { LocalizationService } from 'app/shared/localization/localization.service';
import { AdvMessageService } from 'app/shared/services/message.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-subscriptions-for-business',
  templateUrl: './subscriptions-for-business.component.html',
  styleUrl: './subscriptions-for-business.component.scss'
})
export class SubscriptionsForBusinessComponent implements OnInit {
  roulettes$: Observable<RouletteQuickDetails[]>;
  businessId: number;
  WebRouletteStatus = WebRouletteStatus;
  cancellingRouletteId: number | null = null;
  stoppingRenewalRouletteId: number | null = null;
  rouletteToCancel: RouletteQuickDetails | null = null;
  showCancelConfirm = false;

  constructor(
    private route: ActivatedRoute,
    private webRouletteService: WebRouletteService,
    private subscriptionService: SubscriptionService,
    private localizationService: LocalizationService,
    private messageService: AdvMessageService
  ) {}

  ngOnInit(): void {
    this.businessId = Number(this.route.parent?.snapshot.paramMap.get('id'));
    this.roulettes$ = this.webRouletteService.getRoulettesForBusiness(this.businessId).pipe(
      map(roulettes =>
        roulettes.sort((a, b) => new Date(b.StartDate || 0).getTime() - new Date(a.StartDate || 0).getTime())
          .map(r => {
            r.StartDate = r.StartDate ? new Date(r.StartDate) : null;
            r.EndDate = r.EndDate ? new Date(r.EndDate) : null;
            r.CreatedDate = r.CreatedDate ? new Date(r.CreatedDate) : null;
            return r;
          })
      )
    );
  }

  getPackageTypeLabel(roulette: RouletteQuickDetails): string {
    switch (roulette.PackageType) {
      case 3: return this.localizationService.Translate('SUBSCRIPTION.ROULETTE_FOR_FRONTAL_USE');
      case 2:
      default: return this.localizationService.Translate('SUBSCRIPTION.ROULETTE_FOR_WEB');
    }
  }

  getStatusClass(status: WebRouletteStatus): string {
    switch (status) {
      case WebRouletteStatus.purched:
      case WebRouletteStatus.purchedNotLimitPackage:
      case WebRouletteStatus.purchedFreePackage:
        return 'status-active';
      case WebRouletteStatus.expired:
      case WebRouletteStatus.spinsLimit:
      case WebRouletteStatus.spinsLimitForFreePackage:
        return 'status-expired';
      default:
        return 'status-inactive';
    }
  }

  getActiveCount(roulettes: RouletteQuickDetails[]): number {
    return roulettes.filter(r =>
      r.Status === WebRouletteStatus.purched ||
      r.Status === WebRouletteStatus.purchedNotLimitPackage ||
      r.Status === WebRouletteStatus.purchedFreePackage
    ).length;
  }

  formatDate(date: Date): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('he-IL');
  }

  getPaymentTypeLabel(paymentType: number): string {
    switch (paymentType) {
      case 1: return this.localizationService.Translate('ENUMS.PaymentTypes.OneTime');
      case 2: return this.localizationService.Translate('ENUMS.PaymentTypes.Monthly');
      case 3: return this.localizationService.Translate('ENUMS.PaymentTypes.MultiplePayments');
      case 4: return this.localizationService.Translate('ENUMS.PaymentTypes.Yearly');
      default: return String(paymentType);
    }
  }

  canCancelSubscription(roulette: RouletteQuickDetails): boolean {
    return roulette.PackageType === 3 && !roulette.IsSubscriptionCanceled;
  }

  canStopAutoRenewal(roulette: RouletteQuickDetails): boolean {
    return roulette.PackageType !== 3 && roulette.AutoRenewalPackage === true;
  }

  stopAutoRenewal(roulette: RouletteQuickDetails): void {
    if (!this.canStopAutoRenewal(roulette)) return;

    this.messageService.showConfirmation(
      this.localizationService.Translate('ROULETTE.CHANGE_RENEWAL_TITLE'),
      this.localizationService.Translate('ROULETTE.CHANGE_NOT_RENEWAL_MSG'),
      () => {
        this.stoppingRenewalRouletteId = roulette.Id;
        this.subscriptionService.changeRenewalPackage(roulette.Id, false).subscribe({
          next: () => {
            roulette.AutoRenewalPackage = false;
            this.stoppingRenewalRouletteId = null;
            this.messageService.successMessage(
              this.localizationService.Translate('ROULETTE.CHANGE_NOT_RENEWAL_SUCCESS')
            );
          },
          error: () => {
            this.stoppingRenewalRouletteId = null;
            this.messageService.errorMessage(
              this.localizationService.Translate('ERRORS.GENERAL_ERROR')
            );
          }
        });
      }
    );
  }

  openCancelConfirm(roulette: RouletteQuickDetails): void {
    this.rouletteToCancel = roulette;
    this.showCancelConfirm = true;
  }

  confirmCancel(): void {
    if (!this.rouletteToCancel) return;
    const roulette = this.rouletteToCancel;
    this.cancellingRouletteId = roulette.Id;
    this.showCancelConfirm = false;
    this.subscriptionService.cancelRouletteSubscription(roulette.Id).subscribe({
      next: () => {
        roulette.IsSubscriptionCanceled = true;
        roulette.AutoRenewalPackage = false;
        this.cancellingRouletteId = null;
        this.rouletteToCancel = null;
        this.messageService.successMessage(
          this.localizationService.Translate('ROULETTE.CANCEL_SUBSCRIPTION_SUCCESS')
        );
      },
      error: () => {
        this.cancellingRouletteId = null;
        this.rouletteToCancel = null;
        this.messageService.errorMessage(
          this.localizationService.Translate('ERRORS.GENERAL_ERROR')
        );
      }
    });
  }

  cancelCancelDialog(): void {
    this.showCancelConfirm = false;
    this.rouletteToCancel = null;
  }
}
